import { useState, useEffect } from 'react';
import { 
  Clock, AlertTriangle, CheckCircle2, User, Table2, 
  MessageSquare, ChevronDown, ChevronUp, Flame, Play, Printer
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { printKitchenTicket } from '../../../utils/printing';
import { toast } from 'sonner';

export default function KitchenOrderCard({ 
  order, 
  timeThresholds, 
  onBump, 
  onStart,
  selectedStation 
}) {
  const [elapsed, setElapsed] = useState(0);
  const [expanded, setExpanded] = useState(true);

  // Print handler
  const handlePrint = (e) => {
    e.stopPropagation();
    printKitchenTicket(order, { station: selectedStation !== 'all' ? selectedStation : null });
    toast.success('Kitchen ticket sent to printer');
  };

  // Calculate elapsed time
  useEffect(() => {
    const orderTime = new Date(order.created_at).getTime();
    
    const updateElapsed = () => {
      const now = Date.now();
      setElapsed(Math.floor((now - orderTime) / 1000));
    };
    
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    
    return () => clearInterval(interval);
  }, [order.created_at]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get urgency level based on elapsed time
  const getUrgency = () => {
    if (elapsed >= timeThresholds.critical) return 'critical';
    if (elapsed >= timeThresholds.urgent) return 'urgent';
    if (elapsed >= timeThresholds.warning) return 'warning';
    return 'normal';
  };

  const urgency = getUrgency();

  // Get card styling based on urgency and status
  const getCardStyle = () => {
    if (order.status === 'ready') {
      return 'bg-green-900/50 border-green-500';
    }
    if (order.status === 'preparing') {
      return 'bg-blue-900/30 border-blue-500';
    }
    
    switch (urgency) {
      case 'critical':
        return 'bg-red-900/50 border-red-500 animate-pulse';
      case 'urgent':
        return 'bg-red-900/30 border-red-500';
      case 'warning':
        return 'bg-yellow-900/30 border-yellow-500';
      default:
        return 'bg-slate-800 border-slate-600';
    }
  };

  // Get timer color
  const getTimerColor = () => {
    if (order.status === 'ready') return 'text-green-400';
    if (order.status === 'preparing') return 'text-blue-400';
    
    switch (urgency) {
      case 'critical':
      case 'urgent':
        return 'text-red-400';
      case 'warning':
        return 'text-yellow-400';
      default:
        return 'text-slate-400';
    }
  };

  // Filter items by station if needed
  const displayItems = selectedStation === 'all' 
    ? order.items 
    : order.items?.filter(item => item.station === selectedStation || !item.station);

  // Check if order has allergy notes
  const hasAllergyNotes = (notes) => {
    if (!notes) return false;
    const allergyKeywords = ['allergy', 'allergic', 'no ', 'without', 'gluten', 'dairy', 'nut', 'peanut', 'shellfish', 'vegan', 'vegetarian'];
    return allergyKeywords.some(keyword => notes.toLowerCase().includes(keyword));
  };

  const orderHasAllergyNotes = hasAllergyNotes(order.notes) || 
    order.items?.some(item => hasAllergyNotes(item.notes));

  return (
    <div 
      className={`rounded-xl border-2 ${getCardStyle()} overflow-hidden transition-all`}
      data-testid={`kitchen-order-${order.id}`}
    >
      {/* Order Header */}
      <div className="p-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {/* Order Number */}
            <span className="text-xl font-bold text-white">
              #{order.id?.slice(-4).toUpperCase()}
            </span>
            
            {/* Order Type Badge */}
            <Badge 
              variant="outline" 
              className={`text-xs ${
                order.order_type === 'delivery' ? 'border-purple-500 text-purple-400' :
                order.order_type === 'takeaway' ? 'border-orange-500 text-orange-400' :
                'border-cyan-500 text-cyan-400'
              }`}
            >
              {order.order_type === 'dine_in' ? 'Dine In' : 
               order.order_type === 'takeaway' ? 'Takeaway' : 'Delivery'}
            </Badge>
            
            {/* Allergy Warning */}
            {orderHasAllergyNotes && (
              <Badge className="bg-red-500 text-white animate-pulse">
                <AlertTriangle className="w-3 h-3 mr-1" />
                ALLERGY
              </Badge>
            )}
          </div>
          
          {/* Timer */}
          <div className={`flex items-center gap-1 ${getTimerColor()} font-mono text-lg`}>
            <Clock className="w-4 h-4" />
            {formatTime(elapsed)}
          </div>
        </div>
        
        {/* Customer Info */}
        <div className="flex items-center gap-4 text-sm text-slate-400">
          {order.customer_name && (
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {order.customer_name}
            </span>
          )}
          {order.table_number && (
            <span className="flex items-center gap-1">
              <Table2 className="w-3 h-3" />
              Table {order.table_number}
            </span>
          )}
        </div>
      </div>

      {/* Order Notes - Prominent Display */}
      {order.notes && (
        <div className={`px-3 py-2 ${
          hasAllergyNotes(order.notes) 
            ? 'bg-red-600 text-white' 
            : 'bg-yellow-500/20 text-yellow-300'
        }`}>
          <div className="flex items-start gap-2">
            {hasAllergyNotes(order.notes) && (
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            )}
            <span className="font-bold text-sm">{order.notes}</span>
          </div>
        </div>
      )}

      {/* Order Items */}
      <div className={`p-3 ${expanded ? '' : 'max-h-24 overflow-hidden'}`}>
        <div className="space-y-2">
          {displayItems?.map((item, index) => (
            <div key={index} className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-orange-400">
                    {item.quantity}x
                  </span>
                  <span className="text-white font-medium">{item.name}</span>
                </div>
                
                {/* Item Notes */}
                {item.notes && (
                  <div className={`mt-1 px-2 py-1 rounded text-sm ${
                    hasAllergyNotes(item.notes)
                      ? 'bg-red-600 text-white font-bold'
                      : 'bg-yellow-500/20 text-yellow-300'
                  }`}>
                    {hasAllergyNotes(item.notes) && (
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                    )}
                    {item.notes}
                  </div>
                )}
              </div>
              
              {/* Station badge if available */}
              {item.station && (
                <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                  {item.station}
                </Badge>
              )}
            </div>
          ))}
        </div>
        
        {/* Expand/Collapse for long orders */}
        {order.items?.length > 4 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="w-full mt-2 text-slate-400 hover:text-white"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-4 h-4 mr-1" /> Show Less
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-1" /> Show All ({order.items.length} items)
              </>
            )}
          </Button>
        )}
      </div>

      {/* Action Buttons */}
      <div className="p-3 border-t border-slate-700/50 flex gap-2">
        {/* Print Button */}
        <Button
          onClick={handlePrint}
          variant="outline"
          size="sm"
          className="border-slate-600 text-slate-300 hover:bg-slate-700"
          data-testid={`print-order-${order.id}`}
        >
          <Printer className="w-4 h-4" />
        </Button>
        
        {order.status === 'pending' || order.status === 'confirmed' ? (
          <>
            <Button
              onClick={onStart}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              data-testid={`start-order-${order.id}`}
            >
              <Play className="w-4 h-4 mr-2" />
              Start
            </Button>
            <Button
              onClick={onBump}
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid={`bump-order-${order.id}`}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Ready
            </Button>
          </>
        ) : order.status === 'preparing' ? (
          <Button
            onClick={onBump}
            className="flex-1 bg-green-600 hover:bg-green-700"
            data-testid={`bump-order-${order.id}`}
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Bump (Ready)
          </Button>
        ) : (
          <div className="flex-1 text-center py-2 text-green-400 font-medium">
            <CheckCircle2 className="w-5 h-5 inline mr-2" />
            Ready for Pickup
          </div>
        )}
      </div>
    </div>
  );
}
